import { forwardRef, useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { IoMdNotificationsOutline } from "react-icons/io";
import {
  MdOutlineSubscriptions,
  MdSystemUpdateAlt,
  MdCampaign,
  MdPayment,
  MdAdsClick,
} from "react-icons/md";
import { FaBlog, FaRocket } from "react-icons/fa";
import { CiUser } from "react-icons/ci";
import { LuUsers } from "react-icons/lu";
import { MdOutlineDashboard } from "react-icons/md";
import { FaUserFriends } from "react-icons/fa";
import { HiUsers } from "react-icons/hi2";
import { FiUserPlus, FiX } from "react-icons/fi";
import { MdOutlineAdsClick } from "react-icons/md";
import { MdOutlineFlag, MdOutlineBlock } from "react-icons/md";
import { SIDEBAR_TABS } from "../constants/sidebarTabs";

const Sidebar = forwardRef(({ onClose }, ref) => {
  const location = useLocation();
  const navigate = useNavigate();

  const [isMobile, setIsMobile] = useState(false);
  const [isClient, setIsClient] = useState(false);

  // Initialize on client side
  useEffect(() => {
    setIsClient(true);
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 770);
    };

    checkIfMobile();
    window.addEventListener("resize", checkIfMobile);

    return () => window.removeEventListener("resize", checkIfMobile);
  }, []);

  // Get user role from localStorage
  const userRole = localStorage.getItem("adminUserRole") || "Admin";

  // Define which tabs each role can see
  const canSeeTab = (tabName) => {
    // Manual Purchase: only Developer
    if (tabName === SIDEBAR_TABS.MANUAL_PURCHASE) {
      return userRole === "Developer";
    }
    // Admin: all tabs except restricted ones handled above
    if (userRole === "Admin") {
      return true;
    }
    // Developer: all tabs including Manual Purchase
    if (userRole === "Developer") {
      return true;
    }
    if (userRole === "Author") {
      return [
        SIDEBAR_TABS.DASHBOARD,
        SIDEBAR_TABS.NOTIFICATIONS,
        SIDEBAR_TABS.BLOG,
        SIDEBAR_TABS.PROMOTIONAL_MESSAGES,
        SIDEBAR_TABS.USER_PROFILES,
        SIDEBAR_TABS.ADS,
      ].includes(tabName);
    }
    if (userRole === "CSR") {
      return [
        SIDEBAR_TABS.DASHBOARD,
        SIDEBAR_TABS.USERS,
        SIDEBAR_TABS.SUBSCRIPTION,
        SIDEBAR_TABS.MATCH_USER,
        SIDEBAR_TABS.USER_PROFILES,
        SIDEBAR_TABS.CONTENT_REPORTS,
        SIDEBAR_TABS.BANNED_USERS,
      ].includes(tabName);
    }
    return false;
  };

  const handleNavigation = (path) => {
    if (location.pathname !== path) {
      navigate(path);
    }
    if (isMobile && onClose) {
      onClose();
    }
  };

  const SidebarContent = () => (
    <div
      ref={ref}
      className={`lg:ml-8 px-2 ml-2 lg:px-4 w-full md:w-auto lg:w-90 h-full min-h-[calc(100vh-120px)] bg-black rounded-xl shadow-xl overflow-y-auto no-scrollbar text-white`}
      style={{ overflowX: "visible" }}
    >
      {/* Close button for mobile */}
      {isMobile && onClose && (
        <div className="flex justify-end p-4">
          <button
            onClick={() => onClose()}
            className="text-white hover:bg-white hover:bg-opacity-20 p-2 rounded-full"
          >
            <FiX className="h-6 w-6" />
          </button>
        </div>
      )}

      <div className="flex flex-col mt-6 md:mt-10 gap-1 md:gap-0 justify-start">
        {canSeeTab(SIDEBAR_TABS.DASHBOARD) && (
          <div
            onClick={() => handleNavigation("/dashboard")}
          >
            <div
              className={`w-full px-4 py-3 text-center
                            cursor-pointer mb-1 flex items-center gap-3
                            ${location.pathname === "/" || location.pathname === "/dashboard"
                  ? "bg-white text-black rounded-full"
                  : "bg-transparent text-white font-[600] hover:text-white hover:bg-white hover:bg-opacity-20 hover:rounded-full"
                }`}
            >
              <div className="md:mr-3">
                <MdOutlineDashboard className="h-4 w-4" />
              </div>
              <div>
                <p className="font-medium text-sm">{SIDEBAR_TABS.DASHBOARD}</p>
              </div>
            </div>
          </div>
        )}
      </div>
      <div className="flex flex-col mt-1 gap-1">
        {canSeeTab(SIDEBAR_TABS.USERS) && (
          <div
            onClick={() => handleNavigation("/topusers")}
          >
            <div
              className={`w-full px-4 py-3 text-center
                                     cursor-pointer mb-1 flex items-center gap-3
                                         ${location.pathname.includes(
                "topusers"
              )
                  ? "bg-white text-black rounded-full"
                  : "bg-transparent text-white font-[600] hover:text-white hover:bg-white hover:bg-opacity-20 hover:rounded-full"
                }`}
            >
              <div className="md:mr-3">
                <CiUser className="h-4 w-4" />
              </div>
              <div>
                <p className="font-medium text-sm">{SIDEBAR_TABS.USERS}</p>
              </div>
            </div>
          </div>
        )}

        {canSeeTab(SIDEBAR_TABS.SUBSCRIPTION) && (
          <div
            onClick={() => handleNavigation("/subscription")}
          >
            <div
              className={`w-full px-4 py-3 text-center
                                     cursor-pointer mb-1 flex items-center gap-3
                                         ${location.pathname.includes(
                "subscription"
              )
                  ? "bg-white text-black rounded-full"
                  : "bg-transparent text-white font-[600] hover:text-white hover:bg-white hover:bg-opacity-20 hover:rounded-full"
                }`}
            >
              <div className="md:mr-3">
                <MdOutlineSubscriptions className="h-4 w-4" />
              </div>
              <div>
                <p className="font-medium text-sm">{SIDEBAR_TABS.SUBSCRIPTION}</p>
              </div>
            </div>
          </div>
        )}

        {canSeeTab(SIDEBAR_TABS.MANUAL_PURCHASE) && (
          <div
            onClick={() => handleNavigation("/manual-purchase")}
          >
            <div
              className={`w-full px-4 py-3 text-center
                                     cursor-pointer mb-1 flex items-center gap-3
                                         ${location.pathname.includes(
                "manual-purchase"
              )
                  ? "bg-white text-black rounded-full"
                  : "bg-transparent text-white font-[600] hover:text-white hover:bg-white hover:bg-opacity-20 hover:rounded-full"
                }`}
            >
              <div className="md:mr-3">
                <MdPayment className="h-4 w-4" />
              </div>
              <div>
                <p className="font-medium text-sm">{SIDEBAR_TABS.MANUAL_PURCHASE}</p>
              </div>
            </div>
          </div>
        )}

        {canSeeTab(SIDEBAR_TABS.NOTIFICATIONS) && (
          <div
            onClick={() => handleNavigation("/notifications")}
          >
            <div
              className={`w-full px-4 py-3 text-center
                                     cursor-pointer mb-1 flex items-center gap-3
                                         ${location.pathname.includes(
                "notifications"
              )
                  ? "bg-white text-black rounded-full"
                  : "bg-transparent text-white font-[600] hover:text-white hover:bg-white hover:bg-opacity-20 hover:rounded-full"
                }`}
            >
              <div className="md:mr-3">
                <IoMdNotificationsOutline className="h-4 w-4" />
              </div>
              <div>
                <p className="font-medium text-sm">{SIDEBAR_TABS.NOTIFICATIONS}</p>
              </div>
            </div>
          </div>
        )}
        {canSeeTab(SIDEBAR_TABS.PROMOTIONAL_MESSAGES) && (
          <div
            onClick={() => handleNavigation("/promotional-messages")}
          >
            <div
              className={`w-full px-4 py-3 text-center
                                     cursor-pointer mb-1 flex items-center gap-3
                                         ${location.pathname.includes(
                "promotional-messages"
              )
                  ? "bg-white text-black rounded-full"
                  : "bg-transparent text-white font-[600] hover:text-white hover:bg-white hover:bg-opacity-20 hover:rounded-full"
                }`}
            >
              <div className="md:mr-3">
                <MdCampaign className="h-4 w-4" />
              </div>
              <div>
                <p className="font-medium text-sm">{SIDEBAR_TABS.PROMOTIONAL_MESSAGES}</p>
              </div>
            </div>
          </div>
        )}

        {canSeeTab(SIDEBAR_TABS.ADS) && (
          <div onClick={() => handleNavigation("/ads")}>
            <div
              className={`w-full px-4 py-3 text-center
                                     cursor-pointer mb-1 flex items-center gap-3
                                         ${location.pathname.includes("ads")
                  ? "bg-white text-black rounded-full"
                  : "bg-transparent text-white font-[600] hover:text-white hover:bg-white hover:bg-opacity-20 hover:rounded-full"
                }`}
            >
              <div className="md:mr-3">
                <MdAdsClick className="h-4 w-4" />
              </div>
              <div>
                <p className="font-medium text-sm">{SIDEBAR_TABS.ADS}</p>
              </div>
            </div>
          </div>
        )}

        {canSeeTab(SIDEBAR_TABS.BLOG) && (
          <div
            onClick={() => handleNavigation("/blogs")}
          >
            <div
              className={`w-full px-4 py-3 text-center
                                     cursor-pointer mb-1 flex items-center gap-3
                                         ${location.pathname.includes("blogs")
                  ? "bg-white text-black rounded-full"
                  : "bg-transparent text-white font-[600] hover:text-white hover:bg-white hover:bg-opacity-20 hover:rounded-full"
                }`}
            >
              <div className="md:mr-3">
                <FaBlog className="h-4 w-4" />
              </div>
              <div>
                <p className="font-medium text-sm">{SIDEBAR_TABS.BLOG}</p>
              </div>
            </div>
          </div>
        )}

        {canSeeTab(SIDEBAR_TABS.USER_PROFILES) && (
          <div
            onClick={() => handleNavigation("/user-profiles")}
          >
            <div
              className={`w-full px-4 py-3 text-center
                                     cursor-pointer mb-1 flex items-center gap-3
                                         ${location.pathname.includes(
                "/user-profiles"
              )
                  ? "bg-white text-black rounded-full"
                  : "bg-transparent text-white font-[600] hover:text-white hover:bg-white hover:bg-opacity-20 hover:rounded-full"
                }`}
            >
              <div className="md:mr-3">
                <LuUsers className="h-4 w-4" />
              </div>
              <div>
                <p className="font-medium text-sm">{SIDEBAR_TABS.USER_PROFILES}</p>
              </div>
            </div>
          </div>
        )}

        {canSeeTab(SIDEBAR_TABS.CONTENT_REPORTS) && (
          <div onClick={() => handleNavigation("/content-reports")}>
            <div
              className={`w-full px-4 py-3 text-center
                                     cursor-pointer mb-1 flex items-center gap-3
                                         ${
                location.pathname.includes("/content-reports")
                  ? "bg-white text-black rounded-full"
                  : "bg-transparent text-white font-[600] hover:text-white hover:bg-white hover:bg-opacity-20 hover:rounded-full"
                }`}
            >
              <div className="md:mr-3">
                <MdOutlineFlag className="h-4 w-4" />
              </div>
              <div>
                <p className="font-medium text-sm">{SIDEBAR_TABS.CONTENT_REPORTS}</p>
              </div>
            </div>
          </div>
        )}

        {canSeeTab(SIDEBAR_TABS.BANNED_USERS) && (
          <div onClick={() => handleNavigation("/community-banned-users")}>
            <div
              className={`w-full px-4 py-3 text-center
                                     cursor-pointer mb-1 flex items-center gap-3
                                         ${
                location.pathname.includes("/community-banned-users")
                  ? "bg-white text-black rounded-full"
                  : "bg-transparent text-white font-[600] hover:text-white hover:bg-white hover:bg-opacity-20 hover:rounded-full"
                }`}
            >
              <div className="md:mr-3">
                <MdOutlineBlock className="h-4 w-4" />
              </div>
              <div>
                <p className="font-medium text-sm">{SIDEBAR_TABS.BANNED_USERS}</p>
              </div>
            </div>
          </div>
        )}

        {canSeeTab(SIDEBAR_TABS.MATCH_USER) && (
          <div
            onClick={() => handleNavigation("/match-user")}
          >
            <div
              className={`w-full px-4 py-3 text-center
                                     cursor-pointer mb-1 flex items-center gap-3
                                         ${location.pathname.includes(
                "/match-user"
              )
                  ? "bg-white text-black rounded-full"
                  : "bg-transparent text-white font-[600] hover:text-white hover:bg-white hover:bg-opacity-20 hover:rounded-full"
                }`}
            >
              <div className="md:mr-3">
                <FaUserFriends className="h-4 w-4" />
              </div>
              <div>
                <p className="font-medium text-sm">{SIDEBAR_TABS.MATCH_USER}</p>
              </div>
            </div>
          </div>
        )}

        {canSeeTab(SIDEBAR_TABS.APP_VERSION_CONTROL) && (
          <div
            onClick={() => handleNavigation("/app-version")}
          >
            <div
              className={`w-full px-4 py-3 text-center
                                     cursor-pointer mb-1 flex items-center gap-3
                                         ${location.pathname.includes(
                "/app-version"
              )
                  ? "bg-white text-black rounded-full"
                  : "bg-transparent text-white font-[600] hover:text-white hover:bg-white hover:bg-opacity-20 hover:rounded-full"
                }`}
            >
              <div className="md:mr-3">
                <MdSystemUpdateAlt className="h-4 w-4" />
              </div>
              <div>
                <p className="font-medium text-sm">{SIDEBAR_TABS.APP_VERSION_CONTROL}</p>
              </div>
            </div>
          </div>
        )}

        {canSeeTab(SIDEBAR_TABS.ADMIN_PANEL_USERS) && (
          <div
            onClick={() => handleNavigation("/admin-panel-users")}
          >
            <div
              className={`w-full px-4 py-3 text-center
                                     cursor-pointer mb-1 flex items-center gap-3
                                         ${location.pathname.includes(
                "/admin-panel-users"
              )
                  ? "bg-white text-black rounded-full"
                  : "bg-transparent text-white font-[600] hover:text-white hover:bg-white hover:bg-opacity-20 hover:rounded-full"
                }`}
            >
              <div className="md:mr-3">
                <HiUsers className="h-4 w-4" />
              </div>
              <div>
                <p className="font-medium text-sm">{SIDEBAR_TABS.ADMIN_PANEL_USERS}</p>
              </div>
            </div>
          </div>
        )}

        {canSeeTab(SIDEBAR_TABS.BOOST_HISTORY) && (
          <div
            onClick={() => handleNavigation("/boost-history")}
          >
            <div
              className={`w-full px-4 py-3 text-center
                                     cursor-pointer mb-1 flex items-center gap-3
                                         ${location.pathname.includes(
                "/boost-history"
              )
                  ? "bg-white text-black rounded-full"
                  : "bg-transparent text-white font-[600] hover:text-white hover:bg-white hover:bg-opacity-20 hover:rounded-full"
                }`}
            >
              <div className="md:mr-3">
                <FaRocket className="h-4 w-4" />
              </div>
              <div>
                <p className="font-medium text-sm">{SIDEBAR_TABS.BOOST_HISTORY}</p>
              </div>
            </div>
          </div>
        )}

        {canSeeTab(SIDEBAR_TABS.DUMMY_USERS) && (
          <div
            onClick={() => handleNavigation("/dummy-users")}
          >
            <div
              className={`w-full px-4 py-3 text-center
                                     cursor-pointer mb-1 flex items-center gap-3
                                         ${location.pathname.includes(
                "/dummy-users"
              )
                  ? "bg-white text-black rounded-full"
                  : "bg-transparent text-white font-[600] hover:text-white hover:bg-white hover:bg-opacity-20 hover:rounded-full"
                }`}
            >
              <div className="md:mr-3">
                <FiUserPlus className="h-4 w-4" />
              </div>
              <div>
                <p className="font-medium text-sm">{SIDEBAR_TABS.DUMMY_USERS}</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );





  // Don't render anything until we know if we're on client side
  if (!isClient) {
    return null;
  }

  return <SidebarContent />;
});

export default Sidebar;